import { produce } from 'immer';

import { AusType } from 'constants/loanApplication';

import { RESET_STATE } from 'store/global/actions';

import { AUS_ACTION_TYPE } from '../actions';

const initialState = {
  submitOperations: {},
  // Both keyed by loanId: a summary GET that resolves after the user has moved to another loan
  // must land in its own loan's slot, never overwrite the one on screen.
  ausSummary: {},
  // SETTLED, not "loaded": true once the fetch is DONE, success or failure. A null `ausSummary`
  // means both "no AUS run yet" and "not fetched yet", so consumers deriving an authoritative AUS
  // status need to tell those apart — and a failed fetch must not leave them waiting forever.
  isAusSummarySettled: {},
  isAusSwitching: false,
  findingsList: {
    [AusType.DU]: [],
    [AusType.LP]: [],
  },
  casefileIntake: {
    duCasefileId: null,
    lpAusKey: null,
  },
  mismoPrefill: {
    duCasefileId: null,
    lpAusKey: null,
  },
};

const ausReducer = (state = initialState, { type, payload }) =>
  produce(state, draft => {
    switch (type) {
      case AUS_ACTION_TYPE.ADD_NEW_AUS_SUBMIT_OPERATION:
        draft.submitOperations[payload.operationId] = {
          ...draft[payload.operationId],
          ...payload,
        };
        break;

      case AUS_ACTION_TYPE.GET_AUS_SUBMIT_OPERATIONS:
        draft.submitOperations = payload;
        break;

      case AUS_ACTION_TYPE.GET_AUS_SUMMARY_DATA_PENDING_STATUS: {
        if (!payload?.loanId) {
          break;
        }

        draft.ausSummary[payload.loanId] = null;
        draft.isAusSummarySettled[payload.loanId] = false;
        break;
      }

      case AUS_ACTION_TYPE.GET_AUS_SUMMARY_DATA: {
        if (!payload?.loanId) {
          break;
        }

        draft.ausSummary[payload.loanId] = payload.ausSummary;
        draft.isAusSummarySettled[payload.loanId] = true;
        break;
      }

      // A failed retrieval still settles the signal — consumers gating on it must not hang — and
      // drops the values: showing the previous run's AUS verdict as if it were current is worse
      // than showing none, and a fetch that starts with the pending action already blanked them.
      case AUS_ACTION_TYPE.GET_AUS_SUMMARY_DATA_REJECTED_STATUS: {
        if (!payload?.loanId) {
          break;
        }

        draft.ausSummary[payload.loanId] = null;
        draft.isAusSummarySettled[payload.loanId] = true;
        break;
      }

      case AUS_ACTION_TYPE.SWITCH_AUS_TYPE_PENDING_STATUS: {
        draft.isAusSwitching = true;
        break;
      }

      case AUS_ACTION_TYPE.SWITCH_AUS_TYPE_FULFILLED_STATUS: {
        draft.isAusSwitching = false;
        break;
      }

      case AUS_ACTION_TYPE.GET_AUS_FINDINGS_LIST: {
        const { data, typeOfAus } = payload;

        if (!typeOfAus) {
          break;
        }

        draft.findingsList = {
          ...draft.findingsList,
          [typeOfAus]: data,
        };
        break;
      }

      case AUS_ACTION_TYPE.RESET_AUS_FINDINGS_LIST: {
        draft.findingsList = initialState.findingsList;
        break;
      }

      case AUS_ACTION_TYPE.SET_AUS_CASEFILE_INTAKE: {
        draft.casefileIntake = payload;
        break;
      }

      case AUS_ACTION_TYPE.SET_AUS_MISMO_PREFILL: {
        draft.mismoPrefill = payload;
        break;
      }

      case RESET_STATE:
        return initialState;

      default:
        return state;
    }
  });

export default ausReducer;
